import { Loader, ModBox, Spacer, Text } from '@jfs/ui-new'
import React, { ReactElement } from 'react'
import { FlatList, NativeScrollEvent, NativeSyntheticEvent, StyleProp, ViewStyle } from 'react-native'
import { ListItemRefunds, ListItemSettlement, ListItemTxn } from '../ListRow'
import { styles } from '../ListRow/styles'

export type ListRenderItem<ItemT> = (info: ListRenderItemInfo<ItemT>) => React.ReactElement | null
export interface ListRenderItemInfo<ItemT> {
  item: ItemT
  index: number
  separators: {
    highlight: () => void
    unhighlight: () => void
    updateProps: (select: 'leading' | 'trailing', newProps: any) => void
  }
}

const LocalKeyExtractor = (item: any): string => {
  return item.id
}

interface ListProps {
  data: Array<any>
  disabled: boolean
  onPress: (item: any, index: number) => void
  scrollEnabled?: boolean
  onListFooterPress?: () => void
  isSeeAllVisible?: boolean
  type: string
  loadingState: boolean
  canFetchMore: boolean
  isCustom?: boolean
  RenderCustomElement?: ListRenderItem<any> | null | undefined
  keyExtractor?: (item: any, index: number) => string
  ItemSeparatorComponent?: React.ComponentType<any> | null
  listKey?: string
  customStyle?: StyleProp<ViewStyle>
  testID?: string
  keyboardDismissMode?: 'none' | 'interactive' | 'on-drag'
  keyboardShouldPersistTaps?: boolean | 'always' | 'never' | 'handled'
  extraData?: any
  ListHeaderComponent?: React.ComponentType<any> | React.ReactElement | null
  ListEmptyComponent?: React.ComponentType<any> | React.ReactElement | null
  contentContainerStyle?: StyleProp<ViewStyle>
  numColumns?: number
  horizontal?: boolean
  columnWrapperStyle?: StyleProp<ViewStyle>
  onScroll?: (event: NativeSyntheticEvent<NativeScrollEvent>) => void
  decelerationRate?: 'fast' | 'normal' | number
  disableIntervalMomentum?: boolean
  pagingEnabled?: boolean
  snapToOffsets?: number[]
  getItemLayout?: (
    data: Array<any> | null | undefined,
    index: number,
  ) => { length: number; offset: number; index: number }
  ref?: any
  onMomentumScrollEnd?: (event: NativeSyntheticEvent<NativeScrollEvent>) => void
  overScrollMode?: 'auto' | 'always' | 'never'
  accessibilityLabel?: string
  onEndReached?: ((info: { distanceFromEnd: number }) => void) | null
  onEndReachedThreshold?: number | null
  stickyHeaderIndices?: number[]
  refreshControl?: any
}

const List = ({
  data,
  onPress,
  disabled,
  scrollEnabled = true,
  onListFooterPress,
  isSeeAllVisible,
  type,
  loadingState,
  canFetchMore,
  isCustom,
  RenderCustomElement,
  keyExtractor,
  ItemSeparatorComponent,
  listKey,
  customStyle,
  testID,
  keyboardDismissMode,
  keyboardShouldPersistTaps,
  extraData,
  ListHeaderComponent,
  ListEmptyComponent,
  contentContainerStyle,
  numColumns,
  horizontal,
  columnWrapperStyle,
  onScroll,
  decelerationRate,
  disableIntervalMomentum,
  pagingEnabled,
  snapToOffsets,
  getItemLayout,
  ref,
  onMomentumScrollEnd,
  overScrollMode,
  onEndReached,
  accessibilityLabel,
  onEndReachedThreshold,
  stickyHeaderIndices,
  refreshControl,
}: ListProps) => {
  const RenderItem = (propsData: any): ReactElement => {
    if (type === 'refunds') {
      return (
        <ListItemRefunds
          disabled={disabled}
          onPress={() => onPress(propsData.item, propsData.index)}
          item={propsData.item}
          index={propsData.index}
        />
      )
    }
    if (type === 'transactions') {
      return (
        <ListItemTxn
          disabled={disabled}
          onPress={() => onPress(propsData.item, propsData.index)}
          item={propsData.item}
          index={propsData.index}
        />
      )
    }
    return (
      <ListItemSettlement
        disabled={disabled}
        onPress={() => onPress(propsData.item, propsData.index)}
        item={propsData.item}
        index={propsData.index}
      />
    )
  }

  const ListFooterComponent = () => {
    if ((data?.length > 1 && isSeeAllVisible) ?? false) {
      return (
        <ModBox
          show={(data?.length > 1 && isSeeAllVisible) ?? false}
          onPress={onListFooterPress}
          {...styles.listFooter}
          underlayColor="WHITE">
          <Text type="h3Bold" color={'BLUE'}>
            {'View All Transactions'}
          </Text>
        </ModBox>
      )
    }
    if (data?.length > 1 && !isSeeAllVisible && canFetchMore) {
      return (
        <ModBox onPress={onListFooterPress} {...styles.listFooter} underlayColor="WHITE" flexDirection="row">
          <Text type="h3Bold" color={'BLUE'}>
            {'View More'}
          </Text>
          <Spacer direction="horizontal" size="SMALL" />
          {loadingState && <Loader />}
        </ModBox>
      )
    }
    return <></>
  }
  return (
    <FlatList
      showsVerticalScrollIndicator={false}
      keyExtractor={keyExtractor ?? LocalKeyExtractor}
      data={data}
      extraData={extraData ?? data}
      scrollEnabled={scrollEnabled}
      keyboardDismissMode={keyboardDismissMode && keyboardDismissMode}
      keyboardShouldPersistTaps={keyboardShouldPersistTaps ?? 'handled'}
      overScrollMode={overScrollMode ?? 'always'}
      renderItem={isCustom && RenderCustomElement ? RenderCustomElement : RenderItem}
      ListFooterComponent={ListFooterComponent}
      ItemSeparatorComponent={ItemSeparatorComponent && ItemSeparatorComponent}
      listKey={listKey}
      style={customStyle && customStyle}
      testID={testID}
      showsHorizontalScrollIndicator={false}
      ListHeaderComponent={ListHeaderComponent}
      ListEmptyComponent={ListEmptyComponent}
      contentContainerStyle={contentContainerStyle}
      numColumns={numColumns}
      initialNumToRender={10}
      maxToRenderPerBatch={3}
      horizontal={horizontal}
      columnWrapperStyle={columnWrapperStyle}
      onScroll={onScroll}
      decelerationRate={decelerationRate}
      disableIntervalMomentum={disableIntervalMomentum}
      pagingEnabled={pagingEnabled}
      snapToOffsets={snapToOffsets}
      getItemLayout={getItemLayout}
      ref={ref}
      onMomentumScrollEnd={onMomentumScrollEnd}
      accessibilityLabel={accessibilityLabel}
      onEndReached={onEndReached}
      onEndReachedThreshold={onEndReachedThreshold}
      updateCellsBatchingPeriod={5}
      stickyHeaderIndices={stickyHeaderIndices}
      refreshControl={refreshControl}
    />
  )
}

export { List }
